// Data validation utilities
import {
  detectCsvDelimiter,
  parseCSVRow,
  parseCsvNumber,
  splitCsvLines,
} from "./csv-utils";
export const validationRules = {
  required: (value: any, fieldName: string) => {
    if (!value || (typeof value === 'string' && value.trim() === '')) {
      return `${fieldName} is required`;
    }
    return null;
  },

  email: (value: string) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (value && !emailRegex.test(value)) {
      return 'Please enter a valid email address';
    }
    return null;
  },

  gstin: (value: string) => {
    const gstinRegex = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/;
    if (value && !gstinRegex.test(value)) {
      return 'Please enter a valid GSTIN (15 characters)';
    }
    return null;
  },

  mobile: (value: string) => {
    const mobileRegex = /^[6-9]\d{9}$/;
    if (value && !mobileRegex.test(value.replace(/\D/g, ''))) {
      return 'Please enter a valid 10-digit mobile number';
    }
    return null;
  },

  dateNotInPast: (value: string, fieldName: string) => {
    if (value && new Date(value) < new Date()) {
      return `${fieldName} cannot be in the past`;
    }
    return null;
  },

  dueDateAfterInvoiceDate: (dueDate: string, invoiceDate: string) => {
    if (dueDate && invoiceDate && new Date(dueDate) < new Date(invoiceDate)) {
      return 'Due date cannot be earlier than invoice date';
    }
    return null;
  },

  positiveNumber: (value: number, fieldName: string) => {
    if (value <= 0) {
      return `${fieldName} must be greater than 0`;
    }
    return null;
  },

  taxRate: (value: number) => {
    if (value < 0 || value > 50) {
      return 'Tax rate must be between 0% and 50%';
    }
    return null;
  }
};

export const validateCSVFormat = (csvContent: string): { valid: boolean; errors: string[] } => {
  const errors: string[] = [];
  const lines = splitCsvLines(csvContent);

  if (lines.length < 2) {
    errors.push("CSV must contain at least a header row and one data row");
    return { valid: false, errors };
  }

  const delimiter = detectCsvDelimiter(lines[0]);
  const headers = parseCSVRow(lines[0], delimiter).map((h) =>
    h.toLowerCase().replace(/^\uFEFF/, "").trim()
  );
  const hasAny = (aliases: string[]) => headers.some((h) => aliases.includes(h));

  if (!hasAny(["item name", "item", "description", "item description"])) {
    errors.push("Missing required column: item/description");
  }
  if (!hasAny(["unit price", "unitprice"])) {
    errors.push("Missing required column: unit price");
  }
  if (!hasAny(["quantity", "qty"])) {
    errors.push("Missing required column: quantity");
  }

  const unitPriceIdx = headers.findIndex(
    (h) => h === "unit price" || h === "unitprice"
  );
  const qtyIdx = headers.findIndex((h) => h === "quantity" || h === "qty");
  if (unitPriceIdx === -1 || qtyIdx === -1) {
    return { valid: false, errors };
  }

  // Validate data rows (same delimiter as header)
  for (let i = 1; i < lines.length; i++) {
    const row = parseCSVRow(lines[i], delimiter);
    // Skip completely empty rows
    if (row.every((cell) => !cell.trim())) continue;

    if (row.length < Math.max(unitPriceIdx, qtyIdx) + 1) {
      errors.push(
        `Row ${i + 1}: Insufficient columns (got ${row.length}, check quotes/" and ; separators)`
      );
      continue;
    }

    const unitPrice = parseCsvNumber(row[unitPriceIdx] ?? "", NaN);
    const qty = parseCsvNumber(row[qtyIdx] ?? "", NaN);

    if (!Number.isFinite(unitPrice) || unitPrice <= 0) {
      errors.push(`Row ${i + 1}: Invalid unit price ("${row[unitPriceIdx] ?? ""}")`);
    }

    // Allow decimal qty (e.g. 0.05, 5.5)
    if (!Number.isFinite(qty) || qty <= 0) {
      errors.push(`Row ${i + 1}: Invalid quantity ("${row[qtyIdx] ?? ""}")`);
    }
  }

  return { valid: errors.length === 0, errors };
};

export const validatePOData = (po: any): { valid: boolean; errors: string[] } => {
  const errors: string[] = [];

  // Validate basic fields
  if (!po.customerId) errors.push('Customer is required');
  if (!po.supplierId) errors.push('Supplier is required');
  if (!po.deliveryAddress || po.deliveryAddress.trim() === '') {
    errors.push('Delivery address is required');
  }
  
  // Validate asset lines
  if (!po.lines || po.lines.length === 0) {
    errors.push('At least one asset line is required');
  } else {
    po.lines.forEach((line: any, index: number) => {
      const lineErrors = [];
      
      // Check item field (required)
      if (!line.item?.trim()) {
        lineErrors.push('Item name is required');
      }
      
      if (!line.unitPrice || line.unitPrice <= 0) {
        lineErrors.push('Unit price must be greater than 0');
      }
      
      if (!line.qty || line.qty <= 0) {
        lineErrors.push('Quantity must be greater than 0');
      }
      
      if (line.cgst < 0 || line.cgst > 50) {
        lineErrors.push('CGST must be between 0% and 50%');
      }
      
      if (line.sgst < 0 || line.sgst > 50) {
        lineErrors.push('SGST must be between 0% and 50%');
      }
      
      if (line.igst < 0 || line.igst > 50) {
        lineErrors.push('IGST must be between 0% and 50%');
      }
      
      if (line.utgst < 0 || line.utgst > 50) {
        lineErrors.push('UTGST must be between 0% and 50%');
      }
      
      if (lineErrors.length > 0) {
        errors.push(`Asset line ${index + 1}: ${lineErrors.join(', ')}`);
      }
    });
  }

  return { valid: errors.length === 0, errors };
};